import React, { FC, useCallback, useState } from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import {
    PropertiesPanelAccordion,
    PropertiesPanelColorProperty,
    PropertiesPanelRangeProperty,
    PropertiesPanelResourceProperty,
    PropertiesPanelSection,
    PropertiesPanelSwitch,
    PropertiesPanelSwitchProperty
} from '../../../../../widgets/propertiesPanel';
import { EnvironmentsIcon } from '../../../../../widgets/icons/scene';
import { useEnvironmentTextures } from './hooks/useEnvironmentTextures';
import InspectorShell from './inspectorShell';

// WORLD — ciò che sta INTORNO al modello: l'ambiente che lo illumina, lo sfondo su cui si
// staglia, il piano su cui poggia. Sono tre cose che nel backoffice vivevano sparse — la
// texture d'ambiente nelle impostazioni, il ground in un flag del motore, lo sfondo da
// nessuna parte — e qui tornano una schermata sola.
//
// Ogni riga scrive sul motore E sul config della scena: il motore per vederlo subito, il
// config perché è quello che il salvataggio manda al server. Scrivere solo sul motore
// significa una modifica che sparisce ricaricando.

// LA TESTATA, I CAPITOLI E LO SLOT NON SI DISEGNANO PIÙ QUI. Erano scritti in questo file — e
// un'altra volta in `effectsEditor`, e un'altra ancora nei pannelli degli oggetti — e a forza di
// riscriverli le copie hanno smesso di assomigliarsi. Ora la testata è `InspectorShell`, il
// capitolo è `PropertiesPanelSection`, lo slot di un asset è `PropertiesPanelResourceProperty`:
// chi li cambia li cambia una volta, per tutti.

const Note = styled.p`
    margin: 6px 0 0;
    color: ${(props) => props.theme.app.primary};
    font-size: 11px;
`;

interface WorldEditorProps {
    sceneEditor?: any;
    zkScene?: any;
    additionalFeatures?: any;
}

const fileNameOf = (url?: string | null) => {
    if (!url) return null;
    const clean = url.split('?')[0];
    return clean.substring(clean.lastIndexOf('/') + 1) || clean;
};

const WorldEditor: FC<WorldEditorProps> = ({ sceneEditor, zkScene, additionalFeatures }) => {
    const generalConfig = zkScene?.getOrCreateGeneralConfig?.();
    const environmentConfig = zkScene?.getOrCreateEnvironmentConfig?.();
    const skyboxConfig = zkScene?.getOrCreateSkyboxConfig?.();
    const shadowsConfig = zkScene?.getOrCreateShadowsConfig?.();
    const groundConfig = zkScene?.getOrCreateGroundConfig?.();

    // Caricare e togliere le due texture d'ambiente è la stessa procedura per tutti e due, e
    // ora sta in un posto solo: prima viveva dentro le Impostazioni, ed è il motivo per cui
    // qui la mappa si poteva vedere ma non sostituire.
    const environment = useEnvironmentTextures(sceneEditor, zkScene, additionalFeatures);

    // Quella salvata nella scena è il punto di partenza; poi comanda l'hook, che sa se ne è
    // appena stata caricata una.
    const savedEnvironmentTexture = generalConfig?.get?.('environmentTexture') ?? null;
    const environmentTexture = environment.environmentTexture === null ? savedEnvironmentTexture : environment.environmentTexture;

    // LE OPZIONI DELL'AMBIENTE SONO UN OGGETTO DEL CONFIG, e da lì si legge e si scrive: è ciò
    // che il salvataggio manda al server. Può non esserci ancora — una scena che non ha mai avuto
    // un ambiente — e allora non c'è niente da leggere e niente su cui scrivere.
    const environmentOptions = generalConfig?.get?.('environmentOptions');

    const [rotation, setRotation] = useState<number>(environmentOptions?.get?.('rotationY') ?? 0);
    const [environmentIntensity, setEnvironmentIntensity] = useState<number>(environmentOptions?.get?.('pbrIntensity') ?? 1);
    const [castShadows, setCastShadows] = useState<boolean>(shadowsConfig?.get?.('enableShadows') ?? true);

    const [transparent, setTransparent] = useState<boolean>((generalConfig?.get?.('backgroundAlpha') ?? 1) === 0);
    const [backgroundColor, setBackgroundColor] = useState<string>(
        generalConfig?.get?.('backgroundColor') ?? environmentConfig?.get?.('backgroundColor') ?? '#EAECF0'
    );
    const [backdrop, setBackdrop] = useState<boolean>(skyboxConfig?.get?.('createSkybox') ?? false);
    const [backdropBlur, setBackdropBlur] = useState<number>(skyboxConfig?.get?.('blur') ?? 0);

    const [ground, setGround] = useState<boolean>(groundConfig?.get?.('createGround') ?? false);
    const [groundColor, setGroundColor] = useState<string>(groundConfig?.get?.('color') ?? '#FFFFFF');
    const [groundOpacity, setGroundOpacity] = useState<number>(groundConfig?.get?.('opacity') ?? 1);
    const [groundShadow, setGroundShadow] = useState<boolean>(groundConfig?.get?.('enableShadow') ?? true);
    const [groundShadowLevel, setGroundShadowLevel] = useState<number>(groundConfig?.get?.('shadowLevel') ?? 0.5);
    const [groundMirror, setGroundMirror] = useState<boolean>(groundConfig?.get?.('enableMirror') ?? false);
    const [groundMirrorBlur, setGroundMirrorBlur] = useState<number>(groundConfig?.get?.('mirrorBlurKernel') ?? 0);
    const [groundSize, setGroundSize] = useState<number>(groundConfig?.get?.('size') ?? 10);

    // LA ROTAZIONE SI SALVA, e prima no. `setSceneEnvironmentTextureRotationY` gira la texture in
    // Babylon e chiede un ridisegno, e basta: il config non lo tocca — a differenza di tutti i
    // `setViewer*` di questo pannello, che lo scrivono da sé. Ed è proprio dal config che questa
    // riga legge il suo valore aprendosi: si ruotava, si salvava, si ricaricava, e si tornava a
    // zero. Il difetto non si vedeva finché non si ricaricava.
    const changeRotation = useCallback(
        (value: number) => {
            setRotation(value);
            sceneEditor?.setSceneEnvironmentTextureRotationY?.(value);
            environmentOptions?.set?.('rotationY', value);
        },
        [sceneEditor, environmentOptions]
    );

    // QUANTO L'AMBIENTE ILLUMINA. In un configuratore PBR la risposta è `pbrIntensity`: il motore,
    // caricando una scena, la prende di lì e la scrive su ogni PBRMaterial come
    // `environmentIntensity`. Quel giro non ha un setter pubblico che lo rifaccia a scena viva,
    // quindi qui si fa la stessa identica cosa: si scrive il config, che è ciò che si salva, e la
    // si applica ai materiali, che è ciò che si vede.
    const changeEnvironmentIntensity = useCallback(
        (value: number) => {
            setEnvironmentIntensity(value);
            environmentOptions?.set?.('pbrIntensity', value);

            (sceneEditor?.getAllMaterials?.() ?? []).forEach((material: any) => {
                if (typeof material?.environmentIntensity === 'number') material.environmentIntensity = value;
            });

            sceneEditor?.setRenderingNeeded?.();
        },
        [sceneEditor, environmentOptions]
    );

    const changeCastShadows = useCallback(
        (value: boolean) => {
            setCastShadows(value);
            sceneEditor?.setShadowsVisibility?.(value);
            shadowsConfig?.set?.('enableShadows', value);
        },
        [sceneEditor, shadowsConfig]
    );

    const changeTransparent = useCallback(
        (value: boolean) => {
            setTransparent(value);
            sceneEditor?.setViewerBackgroundAlpha?.(value ? 0 : 1);
        },
        [sceneEditor]
    );

    const changeBackgroundColor = useCallback(
        (value: string) => {
            setBackgroundColor(value);
            sceneEditor?.setViewerBackgroundColor?.(value);
        },
        [sceneEditor]
    );

    const changeBackdrop = useCallback(
        (value: boolean) => {
            setBackdrop(value);
            sceneEditor?.setViewerSkyboxCreateSkybox?.(value);
        },
        [sceneEditor]
    );

    const changeBackdropBlur = useCallback(
        (value: number) => {
            setBackdropBlur(value);
            sceneEditor?.setViewerSkyboxBlur?.(value);
        },
        [sceneEditor]
    );

    const changeGround = useCallback(
        (value: boolean) => {
            setGround(value);
            sceneEditor?.setViewerGroundCreateGround?.(value);
        },
        [sceneEditor]
    );

    return (
        <InspectorShell kind='world' name={T._('World', 'Admin')} specs={T._('Environment · background · ground', 'Admin')}>
            <PropertiesPanelSection title={T._('Ambience', 'Admin')}>
                <PropertiesPanelAccordion header={T._('Environment map', 'Admin')}>
                    {/* UN AMBIENTE SI GUARDA PRIMA DI REGOLARLO: è una panoramica, ed è la sola
                        anteprima di questo pannello che ha senso mostrare larga. Finché il dato che
                        abbiamo è una URL e basta, il riquadro porta il glifo — quando la miniatura
                        arriverà entra qui dentro, senza toccare nient'altro. */}
                    <PropertiesPanelResourceProperty
                        id='world-env-slot'
                        hero={<EnvironmentsIcon />}
                        preview={<EnvironmentsIcon />}
                        name={fileNameOf(environmentTexture)}
                        caption={environmentTexture ? T._('Scene environment', 'Admin') : undefined}
                        emptyLabel={T._('No environment map', 'Admin')}
                        onPick={environment.uploadEnvironment}
                        onReplace={environment.uploadEnvironment}
                        onClear={environmentTexture ? environment.removeEnvironment : undefined}
                        disabled={environment.isEnvironmentUploading}
                    />
                    <Note>{T._('A .hdr, .dds or .env file, or the six cubemap faces named _px, _py, _pz, _nx, _ny, _nz.', 'Admin')}</Note>
                    {/* QUANTO ILLUMINA, prima di DA DOVE: è la domanda che si fa per prima davanti a
                        un ambiente nuovo, ed è la prima riga anche nel mockup. */}
                    <PropertiesPanelRangeProperty
                        id='world-env-intensity'
                        label={T._('Intensity', 'Admin')}
                        value={environmentIntensity}
                        min={0}
                        max={2}
                        step={0.05}
                        onChange={changeEnvironmentIntensity}
                    />
                    <PropertiesPanelRangeProperty
                        id='world-env-rotation'
                        label={T._('Rotation', 'Admin')}
                        value={rotation}
                        min={0}
                        max={360}
                        step={1}
                        unit='°'
                        onChange={changeRotation}
                    />
                    <PropertiesPanelSwitchProperty
                        id='world-env-shadows'
                        label={T._('Cast shadows', 'Admin')}
                        checked={castShadows}
                        onChange={changeCastShadows}
                    />
                </PropertiesPanelAccordion>

                {/* L'ORDINE È UNA CATENA DI DIPENDENZE, e si legge dall'alto: «Transparent» decide
                    se un fondo c'è; se c'è, «Color» o «Environment backdrop» decidono che cosa sia;
                    «Blur» esiste solo per il secondo, perché si sfuoca un'immagine, non una tinta. */}
                <PropertiesPanelAccordion header={T._('Background', 'Admin')}>
                    <PropertiesPanelSwitchProperty
                        id='world-bg-transparent'
                        label={T._('Transparent', 'Admin')}
                        checked={transparent}
                        onChange={changeTransparent}
                    />
                    <PropertiesPanelColorProperty
                        id='world-bg-color'
                        label={T._('Color', 'Admin')}
                        value={backgroundColor}
                        onChange={changeBackgroundColor}
                        disabled={transparent}
                    />
                    <PropertiesPanelSwitchProperty
                        id='world-bg-backdrop'
                        label={T._('Environment backdrop', 'Admin')}
                        checked={backdrop}
                        onChange={changeBackdrop}
                        disabled={transparent}
                    />
                    {/* Lo skybox è la sua immagine: il fondale può essere l’ambiente della scena
                        oppure una texture sua, e finché non ce n’è una il fondale mostra l’ambiente. */}
                    <PropertiesPanelResourceProperty
                        id='world-skybox-slot'
                        preview={<EnvironmentsIcon />}
                        name={environment.skyboxTexture ? T._('Custom skybox', 'Admin') : undefined}
                        caption={environment.skyboxTexture ? T._('Used as the backdrop', 'Admin') : undefined}
                        emptyLabel={T._('The backdrop shows the environment', 'Admin')}
                        onPick={environment.uploadSkybox}
                        onReplace={environment.uploadSkybox}
                        onClear={environment.skyboxTexture ? environment.removeSkybox : undefined}
                        disabled={environment.isSkyboxUploading || !backdrop}
                    />
                    {/* Spento quando il fondale non c'è: sfocare uno sfondo che non si vede è un
                        comando che promette un effetto che non arriva. */}
                    <PropertiesPanelRangeProperty
                        id='world-bg-blur'
                        label={T._('Blur', 'Admin')}
                        value={backdropBlur}
                        min={0}
                        max={1}
                        step={0.01}
                        onChange={changeBackdropBlur}
                        disabled={!backdrop}
                    />
                </PropertiesPanelAccordion>
            </PropertiesPanelSection>

            <PropertiesPanelSection title={T._('Ground', 'Admin')}>
                {/* IL PIANO C'È O NON C'È, E LO DICE LA TESTATA. Era una riga «Visible» in mezzo
                    alle altre, cioè un parametro fra parametri: ma non è un parametro del piano, è
                    il piano. Sopra l'interruttore che accende il gruppo, sotto le cose che si
                    possono regolare solo quando è acceso. */}
                <PropertiesPanelAccordion
                    header={T._('Plane', 'Admin')}
                    openWhen={ground}
                    trailing={
                        <PropertiesPanelSwitch
                            id='world-ground-visible'
                            ariaLabel={T._('Show the ground plane', 'Admin')}
                            checked={ground}
                            onChange={changeGround}
                        />
                    }
                >
                    <PropertiesPanelColorProperty
                        id='world-ground-color'
                        label={T._('Color', 'Admin')}
                        value={groundColor}
                        onChange={(value) => {
                            setGroundColor(value);
                            sceneEditor?.setViewerGroundColor?.(value);
                        }}
                        disabled={!ground}
                    />
                    <PropertiesPanelRangeProperty
                        id='world-ground-opacity'
                        label={T._('Opacity', 'Admin')}
                        value={groundOpacity}
                        min={0}
                        max={1}
                        step={0.01}
                        onChange={(value) => {
                            setGroundOpacity(value);
                            sceneEditor?.setViewerGroundOpacity?.(value);
                        }}
                        disabled={!ground}
                    />
                    <PropertiesPanelSwitchProperty
                        id='world-ground-shadow'
                        label={T._('Receive shadow', 'Admin')}
                        checked={groundShadow}
                        onChange={(value) => {
                            setGroundShadow(value);
                            sceneEditor?.setViewerGroundEnableShadow?.(value);
                        }}
                        disabled={!ground}
                    />
                    <PropertiesPanelRangeProperty
                        id='world-ground-shadow-level'
                        label={T._('Shadow opacity', 'Admin')}
                        value={groundShadowLevel}
                        min={0}
                        max={1}
                        step={0.01}
                        onChange={(value) => {
                            setGroundShadowLevel(value);
                            sceneEditor?.setViewerGroundShadowLevel?.(value);
                        }}
                        disabled={!ground || !groundShadow}
                    />
                    <PropertiesPanelSwitchProperty
                        id='world-ground-mirror'
                        label={T._('Mirror', 'Admin')}
                        checked={groundMirror}
                        onChange={(value) => {
                            setGroundMirror(value);
                            sceneEditor?.setViewerGroundEnableMirror?.(value);
                        }}
                        disabled={!ground}
                    />
                    {/* UNO SPECCHIO PERFETTO NON ESISTE: quanto sfoca il riflesso è metà di ciò che lo
                        rende credibile, e il dato era nel modello — `mirrorBlurKernel` — senza nessuno che
                        lo mostrasse. Sta sotto il suo interruttore e si spegne con lui. */}
                    <PropertiesPanelRangeProperty
                        id='world-ground-mirror-blur'
                        label={T._('Mirror blur', 'Admin')}
                        value={groundMirrorBlur}
                        min={0}
                        max={64}
                        step={1}
                        onChange={(value) => {
                            setGroundMirrorBlur(value);
                            sceneEditor?.setViewerGroundMirrorBlur?.(value);
                        }}
                        disabled={!ground || !groundMirror}
                    />
                    {/* QUANTO È LARGO IL PIANO. Nel mockup si chiama «Dimension», ed era la sola riga
                        della sua card che non avessimo: il dato c'era, col suo setter che il config lo
                        scrive da sé. */}
                    <PropertiesPanelRangeProperty
                        id='world-ground-size'
                        label={T._('Dimension', 'Admin')}
                        value={groundSize}
                        min={1}
                        max={100}
                        step={1}
                        onChange={(value) => {
                            setGroundSize(value);
                            sceneEditor?.setViewerGroundSize?.(value);
                        }}
                        disabled={!ground}
                    />
                </PropertiesPanelAccordion>
            </PropertiesPanelSection>
        </InspectorShell>
    );
};

export default WorldEditor;
